import { useEffect, useState } from "react";
import { useLocation, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    CalendarCheck,
    Mail,
    Phone,
    MapPin,
    Clock,
    MessageCircle,
    Headset,
    Handshake,
    LogIn,
    CheckCircle2,
    Loader2,
} from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, MaskedLine } from "@/components/Reveal";
import BookDemoModal from "@/components/BookDemoModal";
import { EMAILS, PHONE_PLACEHOLDER, BUSINESS_HOURS, ADDRESS } from "@/constants/site";
import { PRODUCTS } from "@/data/products";

const inputCls =
    "w-full rounded-xl border border-[#E2E8F4] bg-white px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-ember focus:ring-2 focus:ring-ember/20";
const errorCls = "mt-1.5 text-xs font-medium text-red-600";

const EMPTY_FORM = { name: "", classesName: "", mobile: "", email: "", product: "" };
const MOBILE_RE = /^(?:\+91[\s-]?)?[6-9]\d{9}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const validate = (form) => {
    const errors = {};
    if (!form.name.trim()) errors.name = "Please enter your name.";
    if (!form.classesName.trim()) errors.classesName = "Please enter the classes name.";
    if (!form.mobile.trim()) errors.mobile = "Please enter your mobile number.";
    else if (!MOBILE_RE.test(form.mobile.trim()))
        errors.mobile = "Enter a valid 10-digit Indian mobile number (with or without +91).";
    if (!form.email.trim()) errors.email = "Please enter your email id.";
    else if (!EMAIL_RE.test(form.email.trim())) errors.email = "Enter a valid email address, e.g. name@example.com.";
    if (!form.product) errors.product = "Please choose a product.";
    return errors;
};

const Field = ({ id, label, error, children }) => (
    <div>
        <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-ink/80">
            {label} <span className="text-ember" aria-hidden="true">*</span>
        </label>
        {children}
        {error && (
            <p role="alert" data-testid={`error-${id}`} className={errorCls}>
                {error}
            </p>
        )}
    </div>
);

const Contact = () => {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const [demoOpen, setDemoOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (searchParams.get("demo") === "1") setDemoOpen(true);
    }, [searchParams]);

    useEffect(() => {
        if (location.state?.product) {
            setForm((f) => ({ ...f, product: location.state.product }));
        }
    }, [location.state]);

    const set = (key) => (e) => {
        const value = e.target.value;
        setForm((f) => ({ ...f, [key]: value }));
        if (errors[key]) setErrors((err) => ({ ...err, [key]: undefined }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate(form);
        setErrors(errs);
        if (Object.keys(errs).length > 0) {
            toast.error("Please fix the highlighted fields.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    name: form.name.trim(),
                    classes_name: form.classesName.trim(),
                    mobile: form.mobile.trim(),
                    email: form.email.trim(),
                    product: form.product,
                    source: "Contact Us page",
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const d = data.detail;
                const msg = Array.isArray(d)
                    ? d.map((x) => (x.msg || "").replace("Value error, ", "")).join(" ")
                    : typeof d === "string"
                      ? d
                      : "Could not send your enquiry. Please try again.";
                throw new Error(msg);
            }
            setSubmitted(true);
            toast.success("Enquiry received — thank you!");
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setForm(EMPTY_FORM);
        setErrors({});
        setSubmitted(false);
    };

    return (
        <>
            <Seo
                title="Contact Us | Coaching Crafters AI"
                description="Talk to Coaching Crafters AI — sales for pricing, demos and new projects, support for existing customers. Book a demo or send us an enquiry."
                path="/contact"
            />

            {/* HERO */}
            <section className="relative overflow-hidden" data-testid="contact-hero">
                <div
                    className="pointer-events-none absolute -right-40 -top-40 h-[28rem] w-[28rem] rounded-full bg-ember/10 blur-3xl"
                    aria-hidden="true"
                />
                <div className="mx-auto max-w-7xl px-6 py-20 text-center sm:px-10 lg:py-24">
                    <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-ember">Contact Us</p>
                    <h1 className="mt-5 font-serif text-4xl font-bold leading-[1.08] tracking-tight text-ink sm:text-5xl">
                        <MaskedLine delay={0.1}>Let's talk about</MaskedLine>
                        <MaskedLine delay={0.25}>
                            <em className="font-medium italic text-ember">your goals.</em>
                        </MaskedLine>
                    </h1>
                    <Reveal delay={0.35}>
                        <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-ink/60 sm:text-lg">
                            Pricing, demos, new projects or support for something we have already deployed — pick a
                            channel and a real person answers.
                        </p>
                        <button
                            data-testid="contact-book-demo-button"
                            onClick={() => setDemoOpen(true)}
                            className="mt-8 inline-flex items-center gap-2 rounded-full bg-ember px-8 py-4 text-sm font-semibold text-bone shadow-[0_10px_30px_rgba(30,79,163,0.4)] transition-colors hover:bg-ember-dark"
                        >
                            <CalendarCheck className="h-4 w-4" />
                            Book a Demo
                        </button>
                    </Reveal>
                </div>
            </section>

            {/* SALES & SUPPORT */}
            <section className="mx-auto max-w-7xl px-6 sm:px-10" data-testid="sales-support-section">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <Reveal>
                        <div data-testid="sales-card" className="h-full rounded-3xl border border-[#E2E8F4] bg-white p-8 shadow-sm">
                            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ember/10">
                                <Handshake className="h-6 w-6 text-ember" />
                            </span>
                            <h2 className="mt-5 font-serif text-2xl font-semibold text-ink">Sales</h2>
                            <p className="mt-2 text-sm text-ink/55">For pricing, demos and new projects.</p>
                            <ul className="mt-6 space-y-3 text-sm">
                                <li>
                                    <a
                                        href={`mailto:${EMAILS.sales}`}
                                        data-testid="sales-email-link"
                                        className="flex items-center gap-3 text-ink/75 transition-colors hover:text-ember"
                                    >
                                        <Mail className="h-4 w-4 shrink-0 text-ember" /> {EMAILS.sales}
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="tel:+91"
                                        data-testid="sales-phone-link"
                                        className="flex items-center gap-3 text-ink/75 transition-colors hover:text-ember"
                                    >
                                        <Phone className="h-4 w-4 shrink-0 text-ember" /> {PHONE_PLACEHOLDER}
                                    </a>
                                </li>
                                <li className="flex items-center gap-3 text-ink/75" data-testid="sales-whatsapp">
                                    <MessageCircle className="h-4 w-4 shrink-0 text-ember" /> WhatsApp: {PHONE_PLACEHOLDER}
                                </li>
                                <li className="flex items-center gap-3 text-ink/55">
                                    <Clock className="h-4 w-4 shrink-0 text-ember" /> {BUSINESS_HOURS}
                                </li>
                            </ul>
                            <a
                                href={`mailto:${EMAILS.sales}?subject=New%20project%20enquiry`}
                                data-testid="talk-to-sales-button"
                                className="mt-7 inline-flex w-full items-center justify-center rounded-full bg-ember py-3.5 text-sm font-semibold text-bone transition-colors hover:bg-ember-dark"
                            >
                                Talk to Sales
                            </a>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div data-testid="support-card" className="grain relative h-full rounded-3xl bg-obsidian p-8 text-[#F5F5F4]">
                            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ember/20">
                                <Headset className="h-6 w-6 text-ember-soft" />
                            </span>
                            <h2 className="mt-5 font-serif text-2xl font-semibold">Support</h2>
                            <p className="mt-2 text-sm text-[#F5F5F4]/55">For existing customers and AMC support.</p>
                            <ul className="mt-6 space-y-3 text-sm">
                                <li>
                                    <a
                                        href={`mailto:${EMAILS.support}`}
                                        data-testid="support-email-link"
                                        className="flex items-center gap-3 text-[#F5F5F4]/80 transition-colors hover:text-ember"
                                    >
                                        <Mail className="h-4 w-4 shrink-0 text-ember-soft" /> {EMAILS.support}
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="tel:+91"
                                        data-testid="support-phone-link"
                                        className="flex items-center gap-3 text-[#F5F5F4]/80 transition-colors hover:text-ember"
                                    >
                                        <Phone className="h-4 w-4 shrink-0 text-ember-soft" /> {PHONE_PLACEHOLDER}
                                    </a>
                                </li>
                                <li className="flex items-center gap-3 text-[#F5F5F4]/55">
                                    <Clock className="h-4 w-4 shrink-0 text-ember-soft" /> {BUSINESS_HOURS}
                                </li>
                                <li>
                                    <Link
                                        to="/login"
                                        data-testid="support-login-link"
                                        className="flex items-center gap-3 text-ember-soft transition-colors hover:text-ember"
                                    >
                                        <LogIn className="h-4 w-4 shrink-0" /> Existing customer? Log in
                                    </Link>
                                </li>
                            </ul>
                            <a
                                href={`mailto:${EMAILS.support}?subject=Support%20request`}
                                data-testid="get-support-button"
                                className="mt-7 inline-flex w-full items-center justify-center rounded-full border border-ember py-3.5 text-sm font-semibold text-ember-soft transition-colors hover:bg-ember hover:text-bone"
                            >
                                Get Support
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ENQUIRY FORM + OFFICE */}
            <section className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-24" data-testid="contact-form-section">
                <div className="grid grid-cols-1 gap-10 lg:grid-cols-5">
                    <Reveal className="lg:col-span-3">
                        <div className="rounded-3xl border border-[#E2E8F4] bg-white p-8 shadow-sm sm:p-10">
                            {submitted ? (
                                <div className="flex flex-col items-center py-10 text-center" data-testid="enquiry-success">
                                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-ember/10">
                                        <CheckCircle2 className="h-8 w-8 text-ember" />
                                    </span>
                                    <h2 className="mt-5 font-serif text-2xl font-semibold text-ink">
                                        Thank you, {form.name.split(" ")[0]}.
                                    </h2>
                                    <p className="mt-3 max-w-sm text-sm leading-relaxed text-ink/60">
                                        We have received your enquiry and will get back to you within one working day.
                                    </p>
                                    <button
                                        data-testid="enquiry-reset-button"
                                        onClick={resetForm}
                                        className="mt-7 rounded-full border border-ink/15 px-7 py-3 text-sm font-semibold text-ink transition-colors hover:border-ember hover:text-ember"
                                    >
                                        Send another enquiry
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <h2 className="font-serif text-2xl font-semibold text-ink">Send an enquiry</h2>
                                    <p className="mt-2 text-sm text-ink/55">
                                        We reply within one working day. Prefer a walkthrough instead?{" "}
                                        <button
                                            data-testid="form-book-demo-button"
                                            onClick={() => setDemoOpen(true)}
                                            className="font-semibold text-ember underline-offset-4 hover:underline"
                                        >
                                            Book a demo
                                        </button>
                                        .
                                    </p>
                                    <form onSubmit={handleSubmit} noValidate className="mt-7 space-y-5" data-testid="contact-form">
                                        <Field id="name" label="Name" error={errors.name}>
                                            <input
                                                id="name"
                                                data-testid="contact-name-input"
                                                className={inputCls}
                                                placeholder="Your full name"
                                                value={form.name}
                                                onChange={set("name")}
                                                aria-invalid={!!errors.name}
                                            />
                                        </Field>
                                        <Field id="classesName" label="Classes Name" error={errors.classesName}>
                                            <input
                                                id="classesName"
                                                data-testid="contact-classes-name-input"
                                                className={inputCls}
                                                placeholder="e.g. Grade 10 Mathematics, Leadership Cohort"
                                                value={form.classesName}
                                                onChange={set("classesName")}
                                                aria-invalid={!!errors.classesName}
                                            />
                                        </Field>
                                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                            <Field id="mobile" label="Mobile No" error={errors.mobile}>
                                                <input
                                                    id="mobile"
                                                    data-testid="contact-mobile-input"
                                                    type="tel"
                                                    inputMode="tel"
                                                    className={inputCls}
                                                    placeholder="+91 98765 43210"
                                                    value={form.mobile}
                                                    onChange={set("mobile")}
                                                    aria-invalid={!!errors.mobile}
                                                />
                                            </Field>
                                            <Field id="email" label="Email Id" error={errors.email}>
                                                <input
                                                    id="email"
                                                    data-testid="contact-email-input"
                                                    type="email"
                                                    className={inputCls}
                                                    placeholder="you@example.com"
                                                    value={form.email}
                                                    onChange={set("email")}
                                                    aria-invalid={!!errors.email}
                                                />
                                            </Field>
                                        </div>
                                        <Field id="product" label="Product" error={errors.product}>
                                            <select
                                                id="product"
                                                data-testid="contact-product-select"
                                                className={`${inputCls} ${form.product ? "" : "text-ink/35"}`}
                                                value={form.product}
                                                onChange={set("product")}
                                                aria-invalid={!!errors.product}
                                            >
                                                <option value="">Select a product</option>
                                                {PRODUCTS.map((p) => (
                                                    <option key={p.slug} value={p.name}>
                                                        {p.name}
                                                    </option>
                                                ))}
                                                <option value="Other">Other</option>
                                            </select>
                                        </Field>
                                        <button
                                            type="submit"
                                            data-testid="contact-submit-button"
                                            disabled={submitting}
                                            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ember py-3.5 text-sm font-semibold text-bone shadow-[0_8px_20px_rgba(30,79,163,0.35)] transition-colors hover:bg-ember-dark disabled:cursor-not-allowed disabled:opacity-70"
                                        >
                                            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                            {submitting ? "Sending…" : "Submit Enquiry"}
                                        </button>
                                    </form>
                                </>
                            )}
                        </div>
                    </Reveal>
                    <Reveal delay={0.15} className="lg:col-span-2">
                        <div className="grain relative h-full rounded-3xl bg-obsidian p-8 text-[#F5F5F4] sm:p-10">
                            <h2 className="font-serif text-2xl font-semibold">Visit or write</h2>
                            <ul className="mt-7 space-y-6 text-sm">
                                <li className="flex items-start gap-4">
                                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-ember-soft" />
                                    <span className="leading-relaxed text-[#F5F5F4]/70" data-testid="office-address">
                                        {ADDRESS}
                                    </span>
                                </li>
                                <li className="flex items-start gap-4">
                                    <Mail className="mt-0.5 h-5 w-5 shrink-0 text-ember-soft" />
                                    <a
                                        href={`mailto:${EMAILS.info}`}
                                        data-testid="office-email-link"
                                        className="text-[#F5F5F4]/70 transition-colors hover:text-ember"
                                    >
                                        {EMAILS.info}
                                    </a>
                                </li>
                                <li className="flex items-start gap-4">
                                    <Phone className="mt-0.5 h-5 w-5 shrink-0 text-ember-soft" />
                                    <span className="text-[#F5F5F4]/70" data-testid="office-phone">
                                        {PHONE_PLACEHOLDER}
                                    </span>
                                </li>
                                <li className="flex items-start gap-4">
                                    <Clock className="mt-0.5 h-5 w-5 shrink-0 text-ember-soft" />
                                    <span className="text-[#F5F5F4]/70">{BUSINESS_HOURS}</span>
                                </li>
                            </ul>
                            <div className="mt-10 rounded-2xl border border-white/10 bg-obsidian-card p-6">
                                <p className="font-serif text-lg italic text-ember-soft">Achieving all goals</p>
                                <p className="mt-2 text-xs leading-relaxed text-[#F5F5F4]/50">
                                    Coaching Crafters AI is a brand of Sarvacharya Education Pvt Ltd. Every enquiry is
                                    read by a human.
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <BookDemoModal open={demoOpen} onOpenChange={setDemoOpen} product={form.product} />
        </>
    );
};

export default Contact;
